/**
 * useGeoRestriction Hook
 *
 * Provides geo-restriction state and feature-check helpers.
 * Convenience hook that wraps GeoContext.
 *
 * Separated from GeoContext.tsx for React Fast Refresh compatibility.
 */

import { useContext } from 'react';
import { GeoContext } from '../contexts/GeoContext';
import type { IGeoContext } from '../contexts/GeoContext';

/**
 * Hook to access geo-restriction context.
 *
 * @returns Geo-restriction context with tier info and feature checks
 * @throws Error if used outside GeoProvider
 *
 * @example
 * ```tsx
 * const { tier, isFeatureAllowed, isMinimum } = useGeoRestriction();
 *
 * if (!isFeatureAllowed('group_chat')) {
 *   return <BlockedMessage />;
 * }
 * ```
 */
export const useGeoRestriction = (): IGeoContext => {
  const context = useContext(GeoContext);
  if (context === undefined) {
    throw new Error('useGeoRestriction must be used within a GeoProvider');
  }
  return context;
};
